import { DropdownItemProps } from "@/components/Dropdown/Dropdown";
import { translate } from "@/helper/translate";
import { IStructuredJobData } from "@/types";

export enum NewResumeModalStep {
	CHOICE = 1,
	RESUME_DETAILS = 2,
	DETAILS_LOADING = 3,
	JOB_DETAILS = 4,
	JOB_DETAILS_LOADING = 5,
	TEMPLATE = 6,
	IMPORT_LOADING = 7,
}

export const MIN_JD_CHARS = 80;
export const MIN_RESUME_TEXT_CHARS = 80;
export const MAX_RESUME_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
export const ALLOWED_RESUME_FILE_TYPES = [
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
	// "application/msword", // .doc
];

export const JOB_DESCRIPTION_ERROR_MESSAGE = "We couldn’t detect a valid job description. Please make sure you’ve included enough detail, such as the job title, responsibilities, and requirements. Try pasting the full job description from the original job post and try again.";

export const jobDescriptionExampleOptions: DropdownItemProps[] = [
	{
		title: "Software Engineer",
		id: "software-engineer",
	},
	{
		title: "Data Scientist",
		id: "data-scientist",
	},
	{
		title: "Designer",
		id: "designer",
	},
	{
		title: "Financial Analyst",
		id: "financial-analyst",
	},
	{
		title: "Project Management",
		id: "project-management",
	},
	{
		title: "Sales",
		id: "sales",
	},
	{
		title: "Teacher",
		id: "teacher",
	},
];

const softwareEngineerJobDescription = () => `Job Title: Software Engineer

Job Summary:
We are seeking a skilled Software Engineer to design, develop, and maintain high-quality software solutions. The ideal candidate will have strong programming skills, experience with modern development frameworks, and a passion for building scalable and efficient applications.

Responsibilities:
	•	Develop, test, and maintain software applications following best practices.
	•	Write clean, efficient, and maintainable code in programming languages such as Java, Python, JavaScript, or C#.
	•	Collaborate with cross-functional teams to define requirements and deliver high-quality solutions.
	•	Troubleshoot and debug software issues to ensure optimal performance.
	•	Participate in code reviews and provide constructive feedback to improve code quality.
	•	Implement and maintain APIs and database systems.
	•	Stay updated on emerging technologies and industry trends to improve development practices.

Requirements:
	•	Bachelor’s degree in Computer Science, Software Engineering, or a related field.
	•	Proficiency in at least one major programming language (e.g., Java, Python, JavaScript).
	•	Experience with front-end and/or back-end development frameworks (e.g., React, Node.js, Spring Boot, Django).
	•	Knowledge of database management systems (SQL, NoSQL).
	•	Familiarity with cloud platforms (AWS, GCP, or Azure) is a plus.
	•	Strong problem-solving skills and the ability to work independently or as part of a team.
`;

const softwateEngineerJobInsights: IStructuredJobData = {
	"summary": "The role of Software Engineer involves designing, developing, and maintaining scalable software applications. The position requires strong programming skills and collaboration with cross-functional teams to deliver efficient solutions.",
	"responsibilities": [
		"Develop, test, and maintain software applications using industry best practices.",
		"Write clean, maintainable code in languages such as Java, Python, JavaScript, or C#.",
		"Collaborate with cross-functional teams to gather and define technical requirements.",
		"Troubleshoot and debug issues to ensure high software performance and reliability.",
		"Participate in code reviews to uphold coding standards and share feedback.",
		"Implement and maintain APIs and database systems.",
		"Stay current with emerging technologies and contribute to process improvements."
	],
	"requirements": [
		"Bachelor’s degree in Computer Science, Software Engineering, or a related field.",
		"Proficiency in at least one major programming language (Java, Python, JavaScript).",
		"Experience with front-end and/or back-end frameworks such as React, Node.js, Spring Boot, or Django.",
		"Knowledge of database systems including SQL and NoSQL.",
		"Strong problem-solving skills and ability to work independently or in a team setting."
	],
	"niceToHave": [
		"Familiarity with cloud platforms such as AWS, GCP, or Azure."
	],
	"companyDetails": "",
	"keywords": [
		"Software Development",
		"Java",
		"Python",
		"JavaScript",
		"C#",
		"React",
		"Node.js",
		"Spring Boot",
		"Django",
		"SQL",
		"NoSQL",
		"API Development",
		"Cloud Platforms",
		"AWS",
		"GCP",
		"Azure",
		"Debugging",
		"Code Review",
		"Cross-Functional Collaboration"
	]
};

const dataScientistJobDescription = () => `Job Title: Data Scientist

Job Summary:
We are looking for a highly analytical and results-driven Data Scientist to help transform data into actionable insights. The ideal candidate will have expertise in statistical analysis, machine learning, and data ${translate("visualisation", "lower")}, with the ability to solve complex business problems using data-driven approaches.

Responsibilities:
	•	Collect, clean, and ${translate("analyse", "lower")} large datasets to extract meaningful insights.
	•	Develop and implement machine learning models to support business objectives.
	•	Apply statistical techniques to identify trends, patterns, and correlations.
	•	Build and maintain dashboards and ${translate("visualizations")} to communicate findings.
	•	Collaborate with cross-functional teams to ${translate("optimise")} data-driven decision-making.
	•	Work with data engineers to improve data pipelines and infrastructure.
	•	Stay up to date with advancements in AI, machine learning, and data science best practices.

Requirements:
	•	Bachelor’s or Master’s degree in Data Science, Computer Science, Mathematics, or a related field.
	•	Strong programming skills in Python or R, with experience in SQL.
	•	Knowledge of machine learning frameworks such as TensorFlow, PyTorch, or Scikit-Learn.
	•	Experience with data visualization tools like Tableau, Power BI, or Matplotlib.
	•	Understanding of cloud computing platforms (AWS, GCP, or Azure) is a plus.
	•	Excellent problem-solving and communication skills.
`;

const dataScientistJobInsights: IStructuredJobData = {
	"summary": "The Data Scientist role focuses on transforming complex datasets into actionable insights through statistical analysis, machine learning, and data visualization. The position supports data-driven decision-making across the organization and contributes to solving business challenges with innovative data solutions.",
	"responsibilities": [
		"Collect, clean, and analyze large datasets to uncover insights.",
		"Develop and implement machine learning models aligned with business goals.",
		"Apply statistical methods to identify patterns, trends, and correlations.",
		"Build and maintain dashboards and visualizations to present findings.",
		"Collaborate with cross-functional teams to support data-informed decisions.",
		"Work closely with data engineers to enhance data infrastructure and pipelines.",
		"Stay current with advancements in AI, machine learning, and data science methodologies."
	],
	"requirements": [
		"Bachelor’s or Master’s degree in Data Science, Computer Science, Mathematics, or a related discipline.",
		"Proficiency in Python or R, and experience with SQL.",
		"Knowledge of machine learning frameworks such as TensorFlow, PyTorch, or Scikit-Learn.",
		"Experience with data visualization tools including Tableau, Power BI, or Matplotlib.",
		"Strong problem-solving abilities and effective communication skills."
	],
	"niceToHave": [
		"Familiarity with cloud platforms such as AWS, GCP, or Azure."
	],
	"companyDetails": "",
	"keywords": [
		"Data Science",
		"Python",
		"R",
		"SQL",
		"Machine Learning",
		"TensorFlow",
		"PyTorch",
		"Scikit-Learn",
		"Statistical Analysis",
		"Data Visualization",
		"Tableau",
		"Power BI",
		"Matplotlib",
		"AI",
		"Cloud Computing",
		"AWS",
		"GCP",
		"Azure",
		"Data Pipelines",
		"Data Infrastructure"
	]
};

const designerJobDescription = () => `Job Title: Graphic Designer

Job Summary:
We are looking for a creative and detail-oriented Designer to develop visually appealing and user-friendly designs across digital and print media. The ideal candidate will have a strong portfolio showcasing expertise in graphic design, branding, and/or UI/UX design principles.

Responsibilities:
	•	Create compelling visual designs for websites, apps, marketing materials, and branding assets.
	•	Develop wireframes, prototypes, and high-fidelity UI/UX designs for digital products.
	•	Collaborate with product managers, developers, and marketing teams to bring ideas to life.
	•	Ensure consistency in design elements, typography, and color schemes across all platforms.
	•	${translate("Optimise", "title")} user experiences by conducting usability testing and gathering feedback.
	•	Stay up to date with design trends, tools, and best practices.

Requirements:
	•	Bachelor’s degree in Graphic Design, UI/UX Design, or a related field (or equivalent experience).
	•	Proficiency in Adobe Creative Suite (Photoshop, Illustrator, XD, InDesign), Figma, or Sketch.
	•	Experience in UI/UX design with knowledge of wireframing and prototyping tools.
	•	Strong understanding of typography, color theory, and layout design.
	•	Ability to work in a fast-paced environment and manage multiple projects.
	•	Experience with HTML, CSS, and front-end design principles is a plus.
`;

const designerJobInsights: IStructuredJobData = {
	"summary": "The Graphic Designer role focuses on creating visually engaging and user-friendly designs for both digital and print platforms. This position blends branding, UI/UX, and marketing design to support product and marketing initiatives.",
	"responsibilities": [
		"Design visual assets for websites, applications, marketing campaigns, and branding materials.",
		"Develop wireframes, prototypes, and high-fidelity UI/UX designs for digital experiences.",
		"Collaborate with product managers, developers, and marketing teams throughout the design process.",
		"Ensure visual consistency in typography, layout, and color across all platforms.",
		"Optimize user experiences through usability testing and iterative feedback.",
		"Stay current with industry trends, tools, and design best practices."
	],
	"requirements": [
		"Bachelor’s degree in Graphic Design, UI/UX Design, or a related field (or equivalent practical experience).",
		"Proficiency in Adobe Creative Suite (Photoshop, Illustrator, XD, InDesign), Figma, or Sketch.",
		"Experience with UI/UX design, including wireframing and prototyping.",
		"Strong grasp of typography, color theory, and layout principles.",
		"Ability to manage multiple design projects in a fast-paced environment."
	],
	"niceToHave": [
		"Experience with HTML, CSS, and front-end design principles."
	],
	"companyDetails": "",
	"keywords": [
		"Graphic Design",
		"UI/UX Design",
		"Figma",
		"Sketch",
		"Adobe Creative Suite",
		"Photoshop",
		"Illustrator",
		"XD",
		"InDesign",
		"Wireframing",
		"Prototyping",
		"Typography",
		"Color Theory",
		"Layout Design",
		"User Experience",
		"Usability Testing",
		"HTML",
		"CSS",
		"Branding",
		"Visual Design"
	]
};

const financialAnalystJobDescription = () => `Job Title: Financial Analyst

Job Summary:
We are looking for a detail-oriented Financial Analyst to support business decision-making through financial modeling, data analysis, and forecasting. The ideal candidate will have strong analytical skills, financial acumen, and the ability to communicate insights effectively.

Responsibilities:
	•	${translate("Analyse", "title")} financial data and trends to support strategic decision-making.
	•	Develop and maintain financial models for forecasting and budgeting.
	•	Prepare reports and presentations for senior management and stakeholders.
	•	Assess financial risks and opportunities, providing actionable insights.
	•	Conduct variance analysis to compare actual vs. projected performance.
	•	Collaborate with teams across departments to optimize financial planning.

Requirements:
	•	Bachelor’s degree in Finance, Accounting, Economics, or a related field.
	•	Proficiency in Excel, financial modeling, and data visualization tools (Power BI, Tableau).
	•	Strong understanding of financial statements, ratios, and valuation methods.
	•	Experience with ERP systems (SAP, Oracle) is a plus.
	•	Excellent analytical and communication skills.
`;

const financialAnalystJobInsights: IStructuredJobData = {
	"summary": "The Financial Analyst role focuses on supporting strategic decision-making through financial modeling, forecasting, and data analysis. This position requires strong analytical skills and the ability to communicate financial insights to senior stakeholders.",
	"responsibilities": [
		"Analyze financial data and trends to guide business strategy.",
		"Develop and maintain financial models for budgeting and forecasting.",
		"Prepare financial reports and presentations for senior leadership and stakeholders.",
		"Identify financial risks and opportunities with actionable recommendations.",
		"Conduct variance analysis between actual and projected performance.",
		"Collaborate with cross-functional teams to improve financial planning processes."
	],
	"requirements": [
		"Bachelor’s degree in Finance, Accounting, Economics, or a related field.",
		"Proficiency in Microsoft Excel, financial modeling, and data visualization tools such as Power BI or Tableau.",
		"Strong understanding of financial statements, key financial ratios, and valuation techniques.",
		"Excellent analytical and communication skills."
	],
	"niceToHave": [
		"Experience with ERP systems such as SAP or Oracle."
	],
	"companyDetails": "",
	"keywords": [
		"Financial Analysis",
		"Financial Modeling",
		"Forecasting",
		"Budgeting",
		"Variance Analysis",
		"Excel",
		"Power BI",
		"Tableau",
		"Financial Statements",
		"Valuation Methods",
		"SAP",
		"Oracle",
		"ERP Systems",
		"Data Visualization",
		"Strategic Planning"
	]
};

const projectManagementJobDescription = () => `Job Title: Project Manager

Job Summary:
We are looking for a highly organized Project Manager to lead and coordinate projects from initiation to completion. The ideal candidate will have experience in project planning, resource management, and stakeholder communication.

Responsibilities:
	•	Define project scope, objectives, and deliverables.
	•	Develop project timelines, budgets, and resource allocation plans.
	•	Monitor progress, identify risks, and implement mitigation strategies.
	•	Collaborate with cross-functional teams to ensure smooth execution.
	•	Communicate project status to stakeholders and leadership.
	•	Ensure projects meet quality standards and business goals.

Requirements:
	•	Bachelor’s degree in Business, Project Management, or a related field.
	•	Project Management Certification (PMP, PRINCE2) is a plus.
	•	Experience with project management tools (Jira, Trello, Asana, Monday.com).
	•	Strong leadership, organizational, and problem-solving skills.
	•	Ability to manage multiple projects simultaneously in a fast-paced environment.
`;

const projectManagementJobInsights: IStructuredJobData = {
	"summary": "The Project Manager role is responsible for overseeing projects from initiation to completion, ensuring they are delivered on time, within budget, and aligned with business objectives. This position requires strong planning, coordination, and communication skills to manage cross-functional teams and stakeholder expectations.",
	"responsibilities": [
		"Define project scope, objectives, and deliverables in alignment with business goals.",
		"Create and manage detailed project plans including timelines, budgets, and resource allocations.",
		"Track project progress, identify potential risks, and develop mitigation strategies.",
		"Coordinate with cross-functional teams to ensure effective project execution.",
		"Provide regular updates to stakeholders and senior leadership on project status.",
		"Ensure all deliverables meet quality standards and project requirements."
	],
	"requirements": [
		"Bachelor’s degree in Business, Project Management, or a related discipline.",
		"Experience using project management tools such as Jira, Trello, Asana, or Monday.com.",
		"Strong leadership, organizational, and analytical problem-solving skills.",
		"Proven ability to manage multiple projects simultaneously in a dynamic environment."
	],
	"niceToHave": [
		"Project Management Certification (PMP, PRINCE2)."
	],
	"companyDetails": "",
	"keywords": [
		"Project Management",
		"Project Planning",
		"Resource Allocation",
		"Risk Management",
		"Jira",
		"Trello",
		"Asana",
		"Monday.com",
		"Stakeholder Communication",
		"Cross-Functional Collaboration",
		"Timeline Management",
		"Budget Management",
		"PMP",
		"PRINCE2"
	]
};

const salesJobDescription = () => `Job Title: Sales Representative

Job Summary:
We are seeking a results-driven Sales Representative to identify leads, build relationships, and drive revenue growth. The ideal candidate will have strong interpersonal skills and a passion for delivering value to customers.

Responsibilities:
	•	Identify and engage potential customers through prospecting and networking.
	•	Conduct sales presentations and product demonstrations.
	•	Build and maintain long-term relationships with clients.
	•	Negotiate contracts and close deals to meet sales targets.
	•	Track sales performance and provide regular reports.
	•	Stay up to date on market trends and competitor offerings.

Requirements:
	•	Bachelor’s degree in Business, Marketing, or a related field (preferred).
	•	Proven experience in sales, business development, or account management.
	•	Strong communication and negotiation skills.
	•	Ability to work independently and manage multiple leads.
	•	Experience with CRM software (Salesforce, HubSpot, or similar).
`;

const salesJobInsights: IStructuredJobData = {
	"summary": "The Sales Representative role focuses on generating leads, building client relationships, and closing deals to drive revenue growth. This position requires strong interpersonal and negotiation skills to deliver value to customers and meet sales targets.",
	"responsibilities": [
		"Identify and engage potential customers through prospecting and networking.",
		"Conduct compelling sales presentations and product demonstrations.",
		"Establish and maintain long-term client relationships.",
		"Negotiate contracts and close deals to achieve sales goals.",
		"Monitor and report on sales performance metrics.",
		"Research market trends and competitor offerings to inform sales strategies."
	],
	"requirements": [
		"Bachelor’s degree in Business, Marketing, or a related field (preferred).",
		"Proven experience in sales, business development, or account management.",
		"Excellent communication and negotiation skills.",
		"Ability to manage multiple leads and work independently.",
		"Experience with CRM tools such as Salesforce or HubSpot."
	],
	"niceToHave": [],
	"companyDetails": "",
	"keywords": [
		"Sales",
		"Business Development",
		"Lead Generation",
		"Client Relationship Management",
		"Sales Presentations",
		"Negotiation",
		"Sales Targets",
		"CRM",
		"Salesforce",
		"HubSpot",
		"Market Trends",
		"Account Management"
	]
};

const teacherJobDescription = () => `Job Title: Elementary School Teacher

Job Summary:
We are seeking a passionate and dedicated Elementary School Teacher to create a dynamic and engaging learning environment for students. The ideal candidate will have a strong background in education, excellent classroom management skills, and a commitment to fostering academic and personal growth in students.

Responsibilities:
	•	Develop and implement lesson plans that align with curriculum standards.
	•	Create an engaging and inclusive classroom environment that supports student learning.
	•	Assess student progress through tests, assignments, and class participation.
	•	Adapt teaching methods to accommodate diverse learning styles and needs.
	•	Maintain classroom discipline and promote positive student behavior.
	•	Communicate effectively with parents, guardians, and school staff regarding student progress.
	•	Participate in professional development and collaborate with fellow educators.

Requirements:
	•	Bachelor’s degree in Education or a related field.
	•	Teaching certification or licensure (as required by state or country).
	•	Experience in classroom teaching, preferably at the elementary level.
	•	Strong communication, organizational, and problem-solving skills.
	•	Ability to use educational technology and digital learning tools effectively.
`;

const teacherJobInsights: IStructuredJobData = {
	"summary": "The Elementary School Teacher role involves creating an engaging and supportive learning environment for young students. The position emphasizes lesson planning, classroom management, and fostering academic and personal development.",
	"responsibilities": [
		"Design and deliver lesson plans aligned with established curriculum standards.",
		"Establish an inclusive and stimulating classroom environment that promotes learning.",
		"Evaluate student performance through assessments, assignments, and participation.",
		"Adjust teaching strategies to support various learning styles and individual needs.",
		"Manage classroom behavior and encourage positive student conduct.",
		"Communicate regularly with parents, guardians, and school staff about student progress.",
		"Engage in ongoing professional development and collaborate with colleagues."
	],
	"requirements": [
		"Bachelor’s degree in Education or a related field.",
		"Valid teaching certification or licensure, as required by local regulations.",
		"Classroom teaching experience, preferably in an elementary school setting.",
		"Strong skills in communication, organization, and problem-solving.",
		"Proficiency in using educational technology and digital learning tools."
	],
	"niceToHave": [],
	"companyDetails": "",
	"keywords": [
		"Elementary Education",
		"Lesson Planning",
		"Classroom Management",
		"Student Assessment",
		"Inclusive Teaching",
		"Curriculum Standards",
		"Parental Communication",
		"Teaching Certification",
		"Educational Technology",
		"Professional Development",
		"Student Engagement",
		"Differentiated Instruction"
	]
};


export const getJobDescriptionExample = (id: string) => {
	switch (id) {
		case "software-engineer":
			return softwareEngineerJobDescription();
		case "data-scientist":
			return dataScientistJobDescription();
		case "designer":
			return designerJobDescription();
		case "financial-analyst":
			return financialAnalystJobDescription();
		case "project-management":
			return projectManagementJobDescription();
		case "sales":
			return salesJobDescription();
		case "teacher":
			return teacherJobDescription();
		default:
			return "";
	}
};

export const getExampleJobInsights = (id: string): IStructuredJobData | null => {
	switch (id) {
		case "software-engineer":
			return softwateEngineerJobInsights;
		case "data-scientist":
			return dataScientistJobInsights;
		case "designer":
			return designerJobInsights;
		case "financial-analyst":
			return financialAnalystJobInsights;
		case "project-management":
			return projectManagementJobInsights;
		case "sales":
			return salesJobInsights;
		case "teacher":
			return teacherJobInsights;
		default:
			return null;
	}
};